import React, { useEffect, useState } from "react";
import {
  Modal,
  TextInput,
  Label,
  Button,
  Badge,
  Tooltip,
} from "flowbite-react";
import {
  HiOutlineCloudUpload,
  HiOutlineDuplicate,
  HiOutlinePencil,
  HiOutlineQuestionMarkCircle,
  HiEye,
  HiEyeOff,
  HiX,
  HiChevronDown,
  HiChevronUp,
} from "react-icons/hi";
import { getTags, criarTags } from "../../../api/produto";
import UploadFotosProduto from "./UploadFotosProduto";

const formatDateTimeLocal = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (num) => String(num).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const ordenarFotosComCapaPrimeiro = (fotos = []) => {
  return [...fotos].sort((a, b) => {
    if (a.e_capa && !b.e_capa) return -1;
    if (!a.e_capa && b.e_capa) return 1;
    return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
  });
};

const ModalProduto = ({
  produto,
  isOpen,
  onClose,
  onSave,
  onDuplicate,
  canEdit = true,
  isSaving = false,
  onFotosChanged,
}) => {
  const [produtoEditado, setProdutoEditado] = useState(
    produto || {
      referencia: "",
      descricao: "",
      marca: "",
      tamanhos_disponiveis: "",
      estampa_cor: "",
      valor: "",
      valor_promocional: "",
      desconto: "",
      maximo_parcelas: "",
      destaque_ate: "",
      ativo: true,
      tags: [],
    }
  );

  const [tagsDisponiveis, setTagsDisponiveis] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [sugestoes, setSugestoes] = useState([]);
  const [mostrarDetalhes, setMostrarDetalhes] = useState(false);

  useEffect(() => {
    if (produto) {
      const isModoCriacao = !produto.id;
      setProdutoEditado({
        ...produto,
        fotos: isModoCriacao ? [] : produto.fotos || [],
        destaque_ate: formatDateTimeLocal(produto.destaque_ate),
        ativo: produto.ativo !== false,
      });
    } else {
      setProdutoEditado({
        referencia: "",
        descricao: "",
        marca: "",
        tamanhos_disponiveis: "",
        estampa_cor: "",
        valor: "",
        valor_promocional: "",
        desconto: "",
        maximo_parcelas: "",
        destaque_ate: "",
        ativo: true,
        tags: [],
        fotos: [],
      });
    }
  }, [produto]);

  useEffect(() => {
    async function fetchTags() {
      try {
        const { data } = await getTags();
        setTagsDisponiveis(data);
      } catch (error) {
        console.error("Erro ao buscar tags:", error);
      }
    }
    fetchTags();
  }, []);

  const handleChange = (e) => {
    let { name, value } = e.target;

    setProdutoEditado((prev) => {
      const next = { ...prev };

      if (name === "valor") {
        value = value.replace(/[^0-9,]/g, "");
        const partes = value.split(",");
        if (partes.length > 2) {
          value = partes[0] + "," + partes[1].slice(0, 2);
        } else if (partes.length === 2) {
          value = partes[0] + "," + partes[1].slice(0, 2);
        }
        if (value.startsWith(",")) {
          value = "0" + value;
        }
        value = value.replace(",", ".");

        next.valor = value;
        if (!value) {
          next.valor_promocional = "";
        }
        return next;
      }

      if (name === "valor_promocional") {
        value = value.replace(/[^0-9,]/g, "");
        const partes = value.split(",");
        if (partes.length > 2) {
          value = partes[0] + "," + partes[1].slice(0, 2);
        } else if (partes.length === 2) {
          value = partes[0] + "," + partes[1].slice(0, 2);
        }
        if (value.startsWith(",")) {
          value = "0" + value;
        }
        value = value.replace(",", ".");

        next.valor_promocional = value;
        if (value) {
          next.desconto = "";
        }
        return next;
      }

      if (name === "desconto") {
        value = value.replace(/\D/g, "").slice(0, 2);
        value = value === "" ? null : parseInt(value, 10);

        next.desconto = value;
        if (value !== null) {
          next.valor_promocional = "";
        }
        return next;
      }

      if (name === "maximo_parcelas") {
        value = value.replace(/\D/g, "").slice(0, 2);
        next.maximo_parcelas = value === "" ? null : parseInt(value, 10);
        return next;
      }

      if (name === "destaque_ate") {
        next.destaque_ate = value || "";
        return next;
      }

      next[name] = value;
      return next;
    });
  };

  const handleTagInputChange = (e) => {
    const value = e.target.value;
    setTagInput(value);

    if (value.length > 0) {
      setSugestoes(
        tagsDisponiveis.filter((tag) =>
          tag.nome.toLowerCase().includes(value.toLowerCase())
        )
      );
    } else {
      setSugestoes([]);
    }
  };

  const adicionarTag = (tag) => {
    if (!produtoEditado.tags.includes(tag)) {
      setProdutoEditado({
        ...produtoEditado,
        tags: [...produtoEditado.tags, tag],
      });
    }
    setTagInput("");
    setSugestoes([]);
  };

  const removerTag = (tagParaRemover) => {
    setProdutoEditado({
      ...produtoEditado,
      tags: produtoEditado.tags.filter((t) => {
        const nomeT = typeof t === "string" ? t : t.nome;
        const nomeRemover =
          typeof tagParaRemover === "string"
            ? tagParaRemover
            : tagParaRemover.nome;
        return nomeT !== nomeRemover;
      }),
    });
  };

  const handleUploadSuccess = (novasFotos) => {
    if (!Array.isArray(novasFotos) || novasFotos.length === 0) return;

    setProdutoEditado((prev) => ({
      ...prev,
      fotos: ordenarFotosComCapaPrimeiro([...(prev.fotos || []), ...novasFotos]),
    }));
    if (onFotosChanged) onFotosChanged();
  };

  const handleDeleteFotoSuccess = (fotoId) => {
    setProdutoEditado((prev) => ({
      ...prev,
      fotos: ordenarFotosComCapaPrimeiro(
        (prev.fotos || []).filter((foto) => foto.id !== fotoId)
      ),
    }));
    if (onFotosChanged) onFotosChanged();
  };

  const temValorBase =
    produtoEditado.valor !== null &&
    produtoEditado.valor !== undefined &&
    produtoEditado.valor !== "";
  const temDesconto =
    produtoEditado.desconto !== null &&
    produtoEditado.desconto !== undefined &&
    produtoEditado.desconto !== "";
  const temValorPromocional =
    produtoEditado.valor_promocional !== null &&
    produtoEditado.valor_promocional !== undefined &&
    produtoEditado.valor_promocional !== "";

  const handleCoverChange = (fotoAtualizada) => {
    if (!fotoAtualizada?.id) return;

    setProdutoEditado((prev) => {
      const fotosAtualizadas = (prev.fotos || []).map((foto) => {
        if (foto.id === fotoAtualizada.id) {
          return { ...foto, e_capa: !!fotoAtualizada.e_capa };
        }

        if (fotoAtualizada.e_capa) {
          return { ...foto, e_capa: false };
        }

        return foto;
      });

      return {
        ...prev,
        fotos: ordenarFotosComCapaPrimeiro(fotosAtualizadas),
      };
    });

    if (onFotosChanged) onFotosChanged();
  };

  return (
    <Modal show={isOpen} onClose={onClose} size="lg">
      <Modal.Header>
        <div className="flex items-center gap-2">
          {produto?.id ? (
            <>
              <HiOutlinePencil className="w-5 h-5 text-gray-700" />
              <span>Editar Produto</span>
            </>
          ) : (
            <>
              <HiOutlineCloudUpload className="w-5 h-5 text-gray-700" />
              <span>Cadastrar Produto</span>
            </>
          )}
        </div>
      </Modal.Header>
      <Modal.Body>
        <fieldset
          disabled={!canEdit}
          className={`space-y-4 ${!canEdit ? "opacity-70" : ""}`}
        >
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-gray-700">
                  Status no catalogo
                </p>
                <Tooltip content="Produtos inativos nao aparecem no catalogo publico.">
                  <HiOutlineQuestionMarkCircle className="h-4 w-4 text-gray-400" />
                </Tooltip>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={produtoEditado.ativo !== false}
                onClick={() =>
                  setProdutoEditado((prev) => ({
                    ...prev,
                    ativo: prev.ativo === false,
                  }))
                }
                disabled={!canEdit}
                className={`inline-flex min-w-24 items-center justify-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition ${
                  produtoEditado.ativo === false
                    ? "border-gray-300 bg-white text-gray-600 hover:bg-gray-100"
                    : "border-teal-700 bg-teal-700 text-white hover:bg-teal-800"
                } ${!canEdit ? "cursor-not-allowed opacity-60" : ""}`}
              >
                {produtoEditado.ativo === false ? (
                  <HiEyeOff className="h-4 w-4" />
                ) : (
                  <HiEye className="h-4 w-4" />
                )}
                <span>
                  {produtoEditado.ativo === false ? "Inativo" : "Ativo"}
                </span>
              </button>
            </div>
          </div>

          <div className="space-y-4 rounded-lg border border-gray-200 p-4">
            <p className="text-sm font-medium text-gray-700">Dados principais</p>
            <Label htmlFor="referencia">Referencia *</Label>
            <TextInput
              id="referencia"
              name="referencia"
              type="text"
              value={produtoEditado.referencia || ""}
              onChange={handleChange}
              required
            />

            <Label htmlFor="descricao">Descricao *</Label>
            <TextInput
              id="descricao"
              name="descricao"
              type="text"
              value={produtoEditado.descricao || ""}
              onChange={handleChange}
              required
            />

            <Label htmlFor="marca">Marca *</Label>
            <TextInput
              id="marca"
              name="marca"
              type="text"
              value={produtoEditado.marca || ""}
              onChange={handleChange}
              required
            />
          </div>

          <div className="rounded-lg border border-gray-200 p-4">
            <Label htmlFor="tags">Tags</Label>
            <TextInput
              id="tags"
              name="tags"
              type="text"
              value={tagInput}
              autoComplete="off"
              onChange={handleTagInputChange}
              placeholder="Busque uma tag ou crie uma nova."
            />

            {(sugestoes.length > 0 || tagInput.length > 0) && (
              <ul className="bg-white border border-gray-300 rounded mt-1 shadow-md max-h-32 overflow-auto">
                {sugestoes.map((tag) => (
                  <li
                    key={tag.id}
                    className="px-3 py-2 hover:bg-gray-200 cursor-pointer"
                    onMouseDown={() => adicionarTag(tag.nome)}
                  >
                    <span className="text-gray-400 text-xs"> + </span>
                    <span className="text-gray-700">{tag.nome}</span>
                  </li>
                ))}

                {tagInput.length > 0 &&
                  !tagsDisponiveis.some(
                    (tag) => tag.nome.toLowerCase() === tagInput.toLowerCase()
                  ) && (
                    <li
                      className="px-3 py-2 flex items-center gap-2 cursor-pointer rounded-lg bg-purple-200 hover:bg-purple-300 border-t"
                      onMouseDown={async () => {
                        try {
                          const { data } = await criarTags([tagInput]);
                          setTagsDisponiveis([...tagsDisponiveis, ...data]);
                          adicionarTag(tagInput);
                        } catch (err) {
                          console.error("Erro ao criar tag", err);
                        }
                      }}
                    >
                      <span className="text-purple-600 font-medium">Criar tag</span>
                      <span className="text-purple-600">"{tagInput}"</span>
                    </li>
                  )}
              </ul>
            )}

            <p className="text-xs text-purple-500 mt-1">
              Voce pode adicionar varias - Ex: Feminino, Verao, Promocao.
            </p>

            {tagInput === "" && tagsDisponiveis.length > 0 && (
              <div className="my-3">
                <p className="text-xs text-gray-400 mb-1">Mais frequentes:</p>
                <div className="flex flex-wrap gap-2">
                  {tagsDisponiveis.slice(0, 5).map((tag) => (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => adicionarTag(tag.nome)}
                      className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded-full text-gray-600 hover:bg-gray-100"
                    >
                      <span className="text-gray-400">+</span>
                      {tag.nome}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2 mt-2">
              {produtoEditado.tags.map((tag) => {
                const tagNome = typeof tag === "string" ? tag : tag.nome;
                const tagKey = typeof tag === "string" ? tag : tag.id;

                return (
                  <Badge
                    key={tagKey}
                    color="purple"
                    className="inline-flex items-center px-2 py-1 text-sm leading-none"
                  >
                    <span className="inline-flex items-center leading-none">
                      <span className="leading-none">{tagNome}</span>

                      <HiX
                        className="ml-1 w-4 h-4 cursor-pointer opacity-70 hover:opacity-100"
                        onClick={() => removerTag(tag)}
                      />
                    </span>
                  </Badge>
                );
              })}
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 p-4">
            <Button
              color="gray"
              size="xs"
              onClick={() => setMostrarDetalhes((prev) => !prev)}
            >
              {mostrarDetalhes ? (
                <div>
                  <HiChevronUp className="inline-block w-4 h-4 mr-1" />
                  Ocultar Detalhes
                </div>
              ) : (
                <div>
                  <HiChevronDown className="inline-block w-4 h-4 mr-1" />
                  Mais Detalhes do Produto
                </div>
              )}
            </Button>

            {mostrarDetalhes && (
              <div className="mt-4 space-y-4">
                <div>
                  <Label htmlFor="tamanhos_disponiveis">Tamanhos Disponiveis (Opcional)</Label>
                  <TextInput
                    id="tamanhos_disponiveis"
                    name="tamanhos_disponiveis"
                    type="text"
                    value={produtoEditado.tamanhos_disponiveis || ""}
                    onChange={handleChange}
                  />
                  <p className="text-xs text-gray-500">Separe os tamanhos por virgula. Ex.: P, M, G, GG</p>
                </div>

                <div>
                  <Label htmlFor="estampa_cor">Estampa/Cor (Opcional)</Label>
                  <TextInput
                    id="estampa_cor"
                    name="estampa_cor"
                    type="text"
                    value={produtoEditado.estampa_cor || ""}
                    onChange={handleChange}
                  />
                  <p className="text-xs text-gray-500">Exemplo: Azul, Floral, Listrado</p>
                </div>

                <div>
                  <Label htmlFor="valor">Valor (R$) (Opcional)</Label>
                  <TextInput
                    id="valor"
                    name="valor"
                    type="text"
                    value={
                      produtoEditado.valor !== null &&
                      produtoEditado.valor !== undefined &&
                      produtoEditado.valor !== ""
                        ? String(produtoEditado.valor).replace(".", ",")
                        : ""
                    }
                    onChange={handleChange}
                  />
                  <p className="text-xs text-gray-500">
                    O valor promocional só fica disponível depois que o valor base for preenchido.
                  </p>
                </div>

                <div>
                  <Label htmlFor="valor_promocional">
                    Valor Promocional (Opcional)
                  </Label>
                  <TextInput
                    id="valor_promocional"
                    name="valor_promocional"
                    type="text"
                    value={
                      produtoEditado.valor_promocional !== null &&
                      produtoEditado.valor_promocional !== undefined &&
                      produtoEditado.valor_promocional !== ""
                        ? String(produtoEditado.valor_promocional).replace(".", ",")
                        : ""
                    }
                    onChange={handleChange}
                    disabled={!temValorBase || temDesconto}
                  />
                  <p className="text-xs text-gray-500">
                    Preencha esse campo ou o desconto. Os dois não podem ser usados ao mesmo tempo.
                  </p>
                </div>

                <div>
                  <Label htmlFor="desconto">Desconto (%) (Opcional)</Label>
                  <TextInput
                    id="desconto"
                    name="desconto"
                    type="text"
                    value={
                      produtoEditado.desconto !== null &&
                      produtoEditado.desconto !== undefined
                        ? String(produtoEditado.desconto)
                        : ""
                    }
                    onChange={handleChange}
                    disabled={temValorPromocional}
                  />
                  <p className="text-xs text-gray-500">
                    Digite apenas o numero. Ex.: 10 para 10%. Se usar desconto, o valor promocional fica indisponivel.
                  </p>
                </div>

                <div>
                  <Label htmlFor="maximo_parcelas">Maximo de Parcelas (Opcional)</Label>
                  <TextInput
                    id="maximo_parcelas"
                    name="maximo_parcelas"
                    type="text"
                    value={produtoEditado.maximo_parcelas || ""}
                    onChange={handleChange}
                  />
                  <p className="text-xs text-gray-500">Digite o numero maximo de parcelas (ex.: 6)</p>
                </div>

                <div>
                  <Label htmlFor="destaque_ate">Destacar produto ate (Opcional)</Label>
                  <TextInput
                    id="destaque_ate"
                    name="destaque_ate"
                    type="datetime-local"
                    value={produtoEditado.destaque_ate || ""}
                    onChange={handleChange}
                  />
                  <p className="text-xs text-gray-500">
                    Enquanto essa data nao expirar, o produto sobe no topo do catalogo.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 p-4">
            <UploadFotosProduto
              produto={produtoEditado}
              onUploadSuccess={handleUploadSuccess}
              onDeleteSuccess={handleDeleteFotoSuccess}
              onCoverChange={handleCoverChange}
              canUpload={canEdit}
            />
          </div>
        </fieldset>
      </Modal.Body>
      <Modal.Footer>
        <Button
          color="blue"
          onClick={() => onSave(produtoEditado)}
          disabled={!canEdit || isSaving}
          isProcessing={isSaving}
        >
          {isSaving ? (
            produto?.id ? "Salvando..." : "Criando..."
          ) : produto?.id ? (
            <>
              <HiOutlinePencil className="w-5 h-5 mr-2" />
              Salvar
            </>
          ) : (
            <>
              <HiOutlineCloudUpload className="w-5 h-5 mr-2" />
              Criar Produto
            </>
          )}
        </Button>
        {produto?.id && (
          <Button
            color="purple"
            onClick={() => onDuplicate(produtoEditado)}
            disabled={!canEdit || isSaving}
          >
            <HiOutlineDuplicate className="w-5 h-5 mr-2" />
            Duplicar
          </Button>
        )}
        <Button color="gray" onClick={onClose} disabled={isSaving}>
          Cancelar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalProduto;
